import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Loader2,
  ExternalLink,
  Copy as CopyIcon,
  CheckCircle2,
  Globe,
  Pencil,
  Eye,
} from "lucide-react";
import { startImpersonation } from "../../components/ImpersonationBanner";

const API = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) + "/api";

const authHeader = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Safely turn an axios error into a display STRING. FastAPI 422 responses
// put an ARRAY of {type, loc, msg, ...} objects on `detail`; passing that
// array/object straight to toast/JSX throws React error #31 ("objects are
// not valid as a child"). Always collapse to a plain string.
const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const parts = d.map((x) => (typeof x === "string" ? x : x?.msg)).filter(Boolean);
    if (parts.length) return parts.join("; ");
  }
  if (d && typeof d === "object" && d.msg) return d.msg;
  return e?.message || fallback;
};

const _emptyCreateForm = {
  id: "",
  name: "",
  slug: "",
  plan_id: "starter",
  start_trial: true,
  custom_domain: "",
  admin_email: "",
  admin_name: "",
};

export default function SuperAdminTenants() {
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(_emptyCreateForm);
  // After a successful create, hold the response so the Super Admin
  // can copy the generated temp password BEFORE it disappears.
  const [createResult, setCreateResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([
        axios.get(`${API}/super-admin/tenants`, { headers: authHeader() }),
        axios.get(`${API}/super-admin/plans`, { headers: authHeader() }),
      ]);
      setTenants(t.data.tenants || []);
      setPlans(p.data.plans || []);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load tenants"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createTenant = async (e) => {
    e.preventDefault();
    if (!form.id || !form.name || !form.slug) {
      toast.error("ID, name and slug are required");
      return;
    }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/super-admin/tenants`, form, {
        headers: authHeader(),
      });
      toast.success(`Tenant ${form.name} created`);
      // 2026-06 — Warn the super-admin if the admin welcome/credentials
      // email did NOT go out (usually the platform Resend key isn't set
      // in Super Admin Settings), so they can copy the temp password
      // shown on screen and/or configure Resend.
      if (form.admin_email && data?.admin_email_sent && data.admin_email_sent.ok === false) {
        const reason = data.admin_email_sent.reason === 'resend_not_configured'
          ? 'Platform Resend API key is not configured (Super Admin → Settings). Copy the temp password below and share it manually.'
          : `Email not sent (${data.admin_email_sent.reason}). Copy the temp password below.`;
        toast.warning(reason, { duration: 10000 });
      } else if (form.admin_email && data?.admin_email_sent?.ok) {
        toast.success(`Admin credentials emailed to ${form.admin_email}`);
      }
      setCreateResult(data);
      setForm(_emptyCreateForm);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Failed to create tenant"));
    } finally {
      setCreating(false);
    }
  };

  const changePlan = async (tenantId, planId) => {
    try {
      await axios.patch(
        `${API}/super-admin/tenants/${tenantId}/subscription`,
        { plan_id: planId },
        { headers: authHeader() }
      );
      toast.success("Plan updated");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Failed to update plan"));
    }
  };

  const changeStatus = async (tenantId, status) => {
    try {
      await axios.patch(
        `${API}/super-admin/tenants/${tenantId}/subscription`,
        { status },
        { headers: authHeader() }
      );
      toast.success(`Status → ${status}`);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Failed"));
    }
  };

  const copy = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="space-y-8" data-testid="super-admin-tenants-page">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">Tenants</h1>
        <p className="text-sm text-slate-500">
          Manage every academy on the platform.
        </p>
      </header>

      {/* ===== TEMP PASSWORD CARD (shown once after creation) ===== */}
      {createResult && (
        <section
          className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 space-y-3"
          data-testid="tenant-create-result"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div>
                <h3 className="font-semibold">
                  Tenant <span className="font-mono">{createResult.tenant?.id}</span> provisioned
                </h3>
                <p className="text-xs text-slate-600">
                  Copy these credentials NOW — the temp password is only shown once.
                </p>
              </div>
            </div>
            <button
              onClick={() => setCreateResult(null)}
              className="text-xs text-slate-500 hover:text-slate-700"
              data-testid="dismiss-create-result"
            >
              Dismiss
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {createResult.admin_email && (
              <div className="bg-white rounded border border-emerald-200 p-3">
                <div className="text-xs uppercase tracking-wider text-slate-500">
                  Admin email
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-mono">{createResult.admin_email}</span>
                  <button
                    onClick={() => copy(createResult.admin_email, "Email")}
                    className="text-emerald-700 hover:text-emerald-900"
                  >
                    <CopyIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
            {createResult.admin_temp_password && (
              <div className="bg-white rounded border border-emerald-200 p-3">
                <div className="text-xs uppercase tracking-wider text-slate-500">
                  Temp password
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-mono">{createResult.admin_temp_password}</span>
                  <button
                    onClick={() =>
                      copy(createResult.admin_temp_password, "Temp password")
                    }
                    className="text-emerald-700 hover:text-emerald-900"
                    data-testid="copy-temp-password"
                  >
                    <CopyIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
            {createResult.tenant?.custom_domain && (
              <div className="bg-white rounded border border-emerald-200 p-3">
                <div className="text-xs uppercase tracking-wider text-slate-500">
                  Custom domain
                </div>
                <div className="font-mono mt-1">{createResult.tenant.custom_domain}</div>
              </div>
            )}
            <div className="bg-white rounded border border-emerald-200 p-3">
              <div className="text-xs uppercase tracking-wider text-slate-500">
                Seeded
              </div>
              <div className="text-xs mt-1">
                business_config: {String(createResult.seed?.business_config_seeded)} ·{" "}
                lanes: {createResult.seed?.lanes_seeded} ·{" "}
                admin user: {createResult.seed?.admin_user_id ? "yes" : "no"}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== ONBOARDING WIZARD ===== */}
      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Onboard new tenant
        </h2>
        <form className="grid grid-cols-1 md:grid-cols-3 gap-3" onSubmit={createTenant}>
          <input
            data-testid="new-tenant-id"
            placeholder="Tenant ID (eg: acme)"
            value={form.id}
            onChange={(e) => setForm({ ...form, id: e.target.value })}
            className="border border-slate-300 rounded px-3 py-2 text-sm"
          />
          <input
            data-testid="new-tenant-name"
            placeholder="Display Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="border border-slate-300 rounded px-3 py-2 text-sm"
          />
          <input
            data-testid="new-tenant-slug"
            placeholder="Slug (subdomain)"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            className="border border-slate-300 rounded px-3 py-2 text-sm"
          />
          <select
            data-testid="new-tenant-plan"
            value={form.plan_id}
            onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
            className="border border-slate-300 rounded px-3 py-2 text-sm"
          >
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <input
            data-testid="new-tenant-custom-domain"
            placeholder="Custom domain (optional, eg: acme.com)"
            value={form.custom_domain}
            onChange={(e) => setForm({ ...form, custom_domain: e.target.value })}
            className="border border-slate-300 rounded px-3 py-2 text-sm"
          />
          <div />
          <input
            data-testid="new-tenant-admin-email"
            placeholder="Admin email"
            type="email"
            value={form.admin_email}
            onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
            className="border border-slate-300 rounded px-3 py-2 text-sm"
          />
          <input
            data-testid="new-tenant-admin-name"
            placeholder="Admin name"
            value={form.admin_name}
            onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
            className="border border-slate-300 rounded px-3 py-2 text-sm"
          />
          <button
            data-testid="create-tenant-btn"
            disabled={creating}
            type="submit"
            className="bg-slate-900 text-white rounded px-4 py-2 text-sm hover:bg-slate-800 disabled:opacity-50"
          >
            {creating ? (
              <Loader2 className="w-4 h-4 animate-spin mx-auto" />
            ) : (
              "Create + start trial"
            )}
          </button>
        </form>
        <p className="mt-3 text-xs text-slate-500">
          Seeds default business_config, 5 lanes, and an admin user with a
          generated temp password. The admin must change the password on first login.
        </p>
      </section>

      {/* ===== TENANT TABLE ===== */}
      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin inline" /> Loading…
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Slug</th>
                <th className="px-4 py-3 text-left">Plan</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Trial Ends</th>
                <th className="px-4 py-3 text-left">Custom Domain</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tenants.map((t) => (
                <tr
                  key={t.id}
                  data-testid={`tenant-row-${t.id}`}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={(e) => {
                    // iter309-redesign PR 2: whole row is clickable.
                    // Only navigate when the click didn't originate on
                    // an interactive control (select/button/link).
                    const target = e.target;
                    if (target.closest("a,button,select,input")) return;
                    window.location.href = `/super-admin/tenants/${t.id}`;
                  }}
                >
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to={`/super-admin/tenants/${t.id}`}
                      className="inline-flex items-center gap-2 text-slate-800 hover:text-slate-900"
                      data-testid={`tenant-name-link-${t.id}`}
                    >
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: t.branding?.primary_color || "#1e293b" }}
                      />
                      <span className="hover:underline">{t.name}</span>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{t.slug}</td>
                  <td className="px-4 py-3">
                    <select
                      data-testid={`tenant-plan-${t.id}`}
                      value={t.subscription?.plan_id || ""}
                      onChange={(e) => changePlan(t.id, e.target.value)}
                      className="border border-slate-200 rounded px-2 py-1 text-xs"
                    >
                      {plans.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      data-testid={`tenant-status-${t.id}`}
                      value={t.subscription?.status || "active"}
                      onChange={(e) => changeStatus(t.id, e.target.value)}
                      className="border border-slate-200 rounded px-2 py-1 text-xs"
                    >
                      {["trial", "active", "past_due", "suspended", "cancelled"].map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {t.subscription?.trial_ends_at?.slice(0, 10) || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {t.custom_domain ? (
                      <a
                        href={`https://${t.custom_domain}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-emerald-600 hover:text-emerald-800 inline-flex items-center gap-1"
                      >
                        <Globe className="w-3 h-3" />
                        {t.custom_domain}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={async () => {
                        try {
                          await startImpersonation(t.id);
                        } catch (e) {
                          toast.error(errMsg(e, "Failed to start impersonation"));
                        }
                      }}
                      data-testid={`impersonate-${t.id}`}
                      title="Read-only view of this tenant's admin UI — no mutations allowed."
                      className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 hover:bg-amber-50 px-2 py-1 rounded mr-2"
                    >
                      <Eye className="w-3 h-3" />
                      View as Tenant &mdash; Read Only
                    </button>
                    <Link
                      to={`/super-admin/tenants/${t.id}`}
                      data-testid={`manage-tenant-${t.id}`}
                      className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900 hover:bg-slate-100 px-2 py-1 rounded"
                    >
                      <Pencil className="w-3 h-3" />
                      Manage Tenant
                    </Link>
                  </td>
                </tr>
              ))}
              {tenants.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                    No tenants yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
